import { Fragment, FC } from 'react';
import serverVars from 'server-vars';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { Link } from 'dibs-elements/exports/Link';
import { ItemType } from 'dibs-recent-history/exports/types';
import { FormattedMessage } from 'dibs-react-intl';

import { SharedCarousel } from '../SharedCarousel/SharedCarousel';
import { SharedItemsSwiper } from '../SharedItemsSwiper/SharedItemsSwiper';
import { CarouselType } from '../SharedCarousel/SharedCarousel';
import styles from './SharedCarouselWrapper.scss';
import {
    VERTICAL_OFFSET_RESP_IMG,
    HORIZONTAL_OFFSET_RESP_IMG,
} from '../../constants/imageConstants';

import classnames from 'classnames';

export type ItemClick = { index: number; item: ItemType; actionLabel: string };
export type ItemImpression = { index: number; items: [ItemType]; actionLabel: string };

export type ClassNames = {
    wrapper?: string;
    arrow?: string;
    placeholder?: string;
    itemImage?: string;
    title?: string;
    itemLink?: string;
    list?: string;
};

type Props = {
    isClient: boolean;
    classNames?: ClassNames;
    showFullHeader?: boolean;
    itemsPerPage?: number;
    items: ItemType[];
    viewMoreHref: string;
    title: string;
    titleId: string;
    carouselType: CarouselType;
    actionLabel: string;
    onViewMoreCtaClick?: (actionLabel: string) => void;
    onItemClick?: ({ item, index, actionLabel }: ItemClick) => void;
    onItemImpression?: ({ items, index, actionLabel }: ItemImpression) => void;
};

const SWIPER_ITEMS_PER_PAGE = 4.5;

export const SharedCarouselWrapper: FC<Props> = ({
    isClient,
    classNames = {},
    showFullHeader = true,
    itemsPerPage = 6,
    items,
    viewMoreHref,
    title,
    titleId,
    carouselType,
    actionLabel,
    onViewMoreCtaClick = () => {},
    onItemClick,
    onItemImpression,
}) => {
    if (!isClient || !items?.length) {
        return null;
    }

    const itemClasses = classnames(styles.item, { [styles.center]: items.length < 3 });
    const isTablet = serverVars.get('settings.isTablet');
    const maxCarouselItemLength = (isTablet ? SWIPER_ITEMS_PER_PAGE : itemsPerPage) * 2;
    const showViewMoreCta = items.length >= maxCarouselItemLength;
    const titleClassName = classNames?.title || styles?.title;

    return (
        <Fragment>
            <div className={styles.headerContainer}>
                <div className={titleClassName}>
                    <HeadingLevel>
                        {Heading => (
                            <Heading id={titleId} className={styles.title}>
                                {title}
                            </Heading>
                        )}
                    </HeadingLevel>
                </div>
                {showFullHeader && showViewMoreCta && (
                    <Link
                        className={styles.viewMoreLink}
                        href={viewMoreHref}
                        onClick={() => onViewMoreCtaClick(actionLabel)}
                    >
                        <FormattedMessage id="shared.viewMoreCta" defaultMessage="View More" />
                    </Link>
                )}
            </div>
            {isTablet ? (
                <SharedItemsSwiper
                    items={items}
                    itemsPerPage={SWIPER_ITEMS_PER_PAGE}
                    offsetVertical={VERTICAL_OFFSET_RESP_IMG}
                    offsetHorizontal={HORIZONTAL_OFFSET_RESP_IMG}
                    classNames={{
                        wrapper: classNames?.wrapper || styles.swiperWrapper,
                        item: itemClasses,
                        list: items.length > 5 ? '' : styles.list,
                        placeholder: classNames?.placeholder || styles.placeholder,
                        itemImage: classNames?.itemImage || styles.itemImage,
                    }}
                    onItemClick={onItemClick}
                    onItemImpression={onItemImpression}
                    actionLabel={actionLabel}
                />
            ) : (
                <SharedCarousel
                    items={items}
                    itemsPerPage={itemsPerPage}
                    classNames={{
                        list: items.length > itemsPerPage ? '' : styles.list,
                        placeholder: styles.placeholder,
                        itemLink: styles.itemLink,
                        itemImage: styles.itemImage,
                        arrow: classNames?.arrow,
                        wrapper: classNames?.wrapper || styles.carouselWrapper,
                    }}
                    carouselType={carouselType}
                    onItemClick={onItemClick}
                    onItemImpression={onItemImpression}
                    actionLabel={actionLabel}
                />
            )}
        </Fragment>
    );
};
